/** Approval card: tool/args preview, risk, plan, four grant scopes + signature placeholder. */
import { useEffect, useState } from 'react';
import type { ApprovalDecision } from '@luminis/protocol';
import {
  DECISION_HINTS,
  DECISION_LABELS,
  approvalExpiryRatio,
  decisionScopeSummary,
  isExpired,
  isHighRisk,
} from '../lib/approval';
import { fingerprint } from '../lib/crypto/e2e';
import { formatClock, formatCountdown, formatRelative, shortId } from '../lib/format';
import type { ApprovalView } from '../lib/types';
import { useApp } from '../state/AppState';
import { ConfirmDialog } from './ConfirmDialog';
import { Icon } from './Icon';
import { MaskedText } from './MaskedText';
import { RiskBadge } from './RiskBadge';

const GRANT_DECISIONS: ApprovalDecision[] = [
  'allow_once',
  'allow_task',
  'allow_15m',
  'allow_always',
];

const SOURCE_LABELS: Record<ApprovalView['source'], string> = {
  user: '用户指令',
  external: '外部内容',
  system: '系统',
};

const STATUS_STYLES: Record<ApprovalView['status'], string> = {
  pending: 'border-amber-400/40 bg-amber-400/10 text-amber-200',
  granted: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-200',
  denied: 'border-rose-400/40 bg-rose-400/10 text-rose-200',
  expired: 'border-slate-500/40 bg-slate-500/10 text-slate-300',
  revoked: 'border-slate-500/40 bg-slate-500/10 text-slate-300',
};

const STATUS_LABELS: Record<ApprovalView['status'], string> = {
  pending: '待审批',
  granted: '已批准',
  denied: '已拒绝',
  expired: '已过期',
  revoked: '已吊销',
};

const RISK_ACCENTS: Record<ApprovalView['risk'], string> = {
  read: 'bg-emerald-400/60',
  write: 'bg-amber-400/70',
  external: 'bg-sky-400/70',
  destructive: 'bg-rose-500/80',
};

export function ApprovalCard({ approval }: { approval: ApprovalView }) {
  const { approve, pinRecord } = useApp();
  const [, forceTick] = useState(0);
  const [pendingDecision, setPendingDecision] = useState<ApprovalDecision | null>(null);
  const [confirmHighRisk, setConfirmHighRisk] = useState(false);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => forceTick((value) => value + 1), 30_000);
    return () => clearInterval(timer);
  }, []);

  const expired = approval.status === 'pending' && isExpired(approval);
  const effectiveStatus: ApprovalView['status'] = expired ? 'expired' : approval.status;
  const isPending = effectiveStatus === 'pending';
  const highRisk = isHighRisk(approval.risk);

  const openDecision = (decision: ApprovalDecision): void => {
    setPendingDecision(decision);
    setConfirmHighRisk(false);
    setPin('');
    setError(null);
  };

  const submitDecision = async (): Promise<void> => {
    if (!pendingDecision || busy) return;
    setBusy(true);
    setError(null);
    const result = await approve(approval, pendingDecision, {
      confirmHighRisk,
      ...(pin ? { pin } : {}),
    });
    setBusy(false);
    if (result.ok) setPendingDecision(null);
    else setError(result.error ?? '审批处理失败');
  };

  const deny = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await approve(approval, 'deny', { confirmHighRisk: false });
    setBusy(false);
    if (!result.ok) setError(result.error ?? '拒绝失败');
  };

  const dialogDecision = pendingDecision ?? 'allow_once';
  const needPin = Boolean(pinRecord) && pendingDecision !== null;
  const disabledByRisk = highRisk && !confirmHighRisk;
  const expiryRatio = isPending ? approvalExpiryRatio(approval) : 0;
  const expiringSoon = isPending && expiryRatio <= 0.15;
  const delivery = approval.delivery_state;
  const decisionInFlight = delivery === 'sending' || delivery === 'sent';
  const execution = approval.execution;
  const executionLabel =
    execution?.status === 'running'
      ? 'daemon 执行中'
      : execution?.status === 'ok'
        ? 'daemon 执行成功'
        : 'daemon 执行失败';
  const executionTone =
    execution?.status === 'running'
      ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
      : execution?.status === 'ok'
        ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
        : 'border-rose-400/30 bg-rose-400/10 text-rose-200';

  return (
    <article className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0f1629]/95 p-4 shadow-lg shadow-black/20">
      <span
        className={`absolute inset-y-0 left-0 w-1 ${RISK_ACCENTS[approval.risk]}`}
        aria-hidden="true"
      />
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <code className="truncate rounded-md bg-black/30 px-2 py-0.5 font-mono text-[12px] text-indigo-200">
              {approval.tool}
            </code>
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-slate-300">
              {SOURCE_LABELS[approval.source]}
            </span>
          </div>
          <p className="mt-1 text-[10px] text-slate-500">
            {approval.task_id ? `task ${shortId(approval.task_id)} · ` : ''}
            请求于 {formatClock(approval.requested_at)} · id {shortId(approval.approval_id)}
          </p>
          {isPending ? (
            <p className="mt-0.5 text-[10px] text-slate-500">
              已等待 {formatRelative(approval.requested_at)}
              {approval.decided_at ? ` · 处理于 ${formatRelative(approval.decided_at)}` : ''}
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] ${STATUS_STYLES[effectiveStatus]}`}
          >
            {STATUS_LABELS[effectiveStatus]}
          </span>
          <RiskBadge risk={approval.risk} />
        </div>
      </header>

      <p className="mt-3 text-sm leading-relaxed text-slate-100">{approval.summary}</p>

      {execution ? (
        <div className="mt-3 rounded-xl border border-white/5 bg-black/25 p-3 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className={`rounded-full border px-2 py-0.5 text-[10px] ${executionTone}`}>
              {executionLabel}
            </span>
            <span className="text-[10px] text-slate-500">
              {execution.exit_code !== undefined && execution.exit_code !== null
                ? `exit ${execution.exit_code}`
                : ''}
            </span>
          </div>
          {execution.stdout ? (
            <pre className="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap break-words rounded bg-black/30 p-2 text-[11px] text-slate-300">
              {execution.stdout}
            </pre>
          ) : null}
          {execution.stderr ? (
            <pre className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap break-words rounded bg-black/30 p-2 text-[11px] text-rose-200/90">
              {execution.stderr}
            </pre>
          ) : null}
        </div>
      ) : null}

      {approval.source === 'external' ? (
        <p className="mt-2 rounded-lg border border-sky-400/20 bg-sky-400/5 px-2.5 py-1.5 text-[10px] leading-relaxed text-sky-200/80">
          外部内容（网页/文件/聊天记录）不可信：只能触发读取/摘要/提案；写操作必须由你在此审批。
        </p>
      ) : null}

      {approval.args_preview ? (
        <div className="mt-3 rounded-xl border border-white/5 bg-black/30 p-3 text-xs">
          <MaskedText value={approval.args_preview} multiline label="参数预览" />
        </div>
      ) : null}

      {approval.plan && approval.plan.length > 0 ? (
        <details className="mt-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-xs text-slate-300">
          <summary className="cursor-pointer text-slate-400">
            执行计划 · {approval.plan.length} 步
          </summary>
          <ol className="mt-2 list-decimal space-y-1 pl-4">
            {approval.plan.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </details>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[10px] text-slate-500">
        <span className="font-mono">args_hash {shortId(approval.args_hash, 16)}</span>
        <span className={expiringSoon ? 'text-rose-300' : ''}>
          {isPending
            ? `过期倒计时 ${formatCountdown(approval.expires_at)}`
            : `到期 ${formatClock(approval.expires_at)}`}
        </span>
      </div>

      {isPending ? (
        <div
          className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/10"
          role="progressbar"
          aria-label="审批有效期剩余"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(expiryRatio * 100)}
        >
          <div
            className={`h-full rounded-full transition-[width] duration-500 ${
              expiringSoon ? 'bg-rose-500' : 'bg-indigo-400/80'
            }`}
            style={{ width: `${Math.max(2, Math.round(expiryRatio * 100))}%` }}
          />
        </div>
      ) : null}

      {isPending && decisionInFlight ? (
        <div
          className={`mt-4 rounded-xl border px-3 py-2.5 text-xs ${
            delivery === 'sent'
              ? 'border-emerald-400/25 bg-emerald-400/5 text-emerald-200'
              : 'border-indigo-400/25 bg-indigo-400/5 text-indigo-200'
          }`}
        >
          {delivery === 'sent'
            ? '审批决定已发送到 E2E 中继，等待 daemon 验签执行'
            : '正在通过 E2E 中继发送审批决定…'}
          <p className="mt-1 text-[10px] text-slate-500">
            卡片保持待执行状态；执行结果以 daemon 推送/执行活动为准（不预先假装已批准）。
          </p>
        </div>
      ) : isPending ? (
        <div className="mt-4 grid grid-cols-2 gap-2">
          {GRANT_DECISIONS.map((decision) => {
            const blocked = highRisk && decision === 'allow_always';
            return (
              <button
                key={decision}
                type="button"
                disabled={blocked || busy}
                onClick={() => openDecision(decision)}
                className={`flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left text-xs transition ${
                  blocked
                    ? 'cursor-not-allowed border-white/5 bg-white/[0.02] text-slate-600'
                    : 'border-white/10 bg-white/5 text-slate-100 active:bg-white/10'
                } ${decision === 'allow_once' ? 'border-indigo-400/40 bg-indigo-400/10 text-indigo-100' : ''}`}
                title={blocked ? '高危操作不允许永久白名单' : DECISION_HINTS[decision]}
              >
                <span className="font-medium">{DECISION_LABELS[decision]}</span>
                <span className="text-[10px] text-slate-400">
                  {blocked ? '高危不可用' : DECISION_HINTS[decision]}
                </span>
              </button>
            );
          })}
          <button
            type="button"
            disabled={busy}
            onClick={() => void deny()}
            className="col-span-2 rounded-xl border border-rose-400/30 bg-rose-500/10 py-2.5 text-xs font-medium text-rose-200 disabled:opacity-40"
          >
            拒绝并记入审计
          </button>
        </div>
      ) : null}

      {delivery === 'failed' ? (
        <p className="mt-2 rounded-xl border border-rose-400/25 bg-rose-400/5 px-3 py-2 text-xs text-rose-200">
          审批决定发送失败：{approval.decision_error ?? '未知错误'}（可重新选择授权范围重试）
        </p>
      ) : null}

      {approval.status !== 'pending' || expired ? (
        <div className="mt-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] text-slate-400">
          {approval.status === 'pending' && expired ? '已超过 24h 有效期，审批作废。' : null}
          {approval.decision ? <p>授权范围：{decisionScopeSummary(approval.decision)}</p> : null}
          {approval.decided_at ? <p>处理时间：{formatClock(approval.decided_at)}</p> : null}
          {approval.signature ? (
            <p className="mt-1 font-mono text-[10px] text-slate-500">
              签名占位（Ed25519）：{fingerprint(approval.signature, 3)}
            </p>
          ) : null}
        </div>
      ) : null}

      {error ? <p className="mt-2 text-xs text-rose-300">{error}</p> : null}

      <ConfirmDialog
        open={pendingDecision !== null}
        title={`确认：${DECISION_LABELS[dialogDecision]}`}
        confirmLabel={dialogDecision === 'deny' ? '拒绝' : '签名并发送'}
        danger={dialogDecision === 'deny'}
        busy={busy}
        disabled={disabledByRisk || (needPin && pin.length < 4)}
        onCancel={() => setPendingDecision(null)}
        onConfirm={() => void submitDecision()}
      >
        <p>{DECISION_HINTS[dialogDecision]}</p>
        <p className="rounded-lg bg-black/30 px-3 py-2 font-mono text-[10px] text-slate-400">
          args_hash：{shortId(approval.args_hash, 24)}
        </p>
        {highRisk ? (
          <label className="flex items-start gap-2 rounded-lg border border-rose-400/30 bg-rose-500/10 p-2.5 text-xs text-rose-200">
            <input
              type="checkbox"
              className="mt-0.5 accent-rose-500"
              checked={confirmHighRisk}
              onChange={(event) => setConfirmHighRisk(event.target.checked)}
            />
            <span>我确认这是高危操作（{approval.tool}），理解可能造成不可恢复的后果。</span>
          </label>
        ) : null}
        {needPin ? (
          <label className="flex flex-col gap-1 text-xs text-slate-300">
            <span>高危/敏感审批需要重新输入 PIN：</span>
            <input
              type="password"
              inputMode="numeric"
              value={pin}
              onChange={(event) => setPin(event.target.value.replace(/[^0-9]/gu, '').slice(0, 8))}
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-center tracking-[0.4em]"
              placeholder="••••"
            />
          </label>
        ) : null}
        {error ? <p className="text-xs text-rose-300">{error}</p> : null}
        <p className="flex items-center gap-1 text-[10px] text-slate-500">
          <Icon name="shield" className="h-3.5 w-3.5" />
          签名内容绑定 approval_id + decision + args_hash + nonce（Ed25519，验签在 PC daemon 侧）
        </p>
      </ConfirmDialog>
    </article>
  );
}
